/**
 * ResumeMasteryLanding — public marketing page at /resume-mastery.
 * Pitches the AI-native Resume Workspace and funnels into it; the free ATS
 * scorer is the secondary, no-login CTA. Replaced the retired prompt-kit home.
 */
import { Box, Button, Card, CardContent, Container, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link as RouterLink } from "react-router-dom";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

// STEPS/DIFFERENTIATORS are factories so their copy can be translated — call
// them inside the component, which has a translation hook.
function getSteps(t: TFunction): { title: string; body: string }[] {
  return [
    { title: t("ResumeMasteryLanding.step1Title"), body: t("ResumeMasteryLanding.step1Body") },
    { title: t("ResumeMasteryLanding.step2Title"), body: t("ResumeMasteryLanding.step2Body") },
    { title: t("ResumeMasteryLanding.step3Title"), body: t("ResumeMasteryLanding.step3Body") },
    { title: t("ResumeMasteryLanding.step4Title"), body: t("ResumeMasteryLanding.step4Body") },
  ];
}

function getDifferentiators(t: TFunction): { title: string; body: string }[] {
  return [
    { title: t("ResumeMasteryLanding.diff1Title"), body: t("ResumeMasteryLanding.diff1Body") },
    { title: t("ResumeMasteryLanding.diff2Title"), body: t("ResumeMasteryLanding.diff2Body") },
    { title: t("ResumeMasteryLanding.diff3Title"), body: t("ResumeMasteryLanding.diff3Body") },
  ];
}

export default function ResumeMasteryLanding() {
  const theme = useTheme();
  const { t } = useTranslation("pages-account");
  const STEPS = getSteps(t);
  const DIFFERENTIATORS = getDifferentiators(t);
  return (
    <Box>
      {/* Hero */}
      <Box sx={{ background: `linear-gradient(180deg, ${alpha(theme.palette.primary.light, 0.06)}, transparent)`, py: { xs: 6, md: 9 } }}>
        <Container maxWidth="md">
          <Stack spacing={3} alignItems="flex-start">
            <Typography variant="overline" sx={{ color: theme.palette.primary.main, fontWeight: 800, letterSpacing: "0.08em" }}>
              {t("ResumeMasteryLanding.eyebrow")}
            </Typography>
            <Typography variant="h2" fontWeight={500} sx={{ letterSpacing: "-0.02em", lineHeight: 1.1 }}>
              {t("ResumeMasteryLanding.heading")}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 640 }}>
              {t("ResumeMasteryLanding.subheading")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} pt={1}>
              <Button
                component={RouterLink}
                to="/resume-mastery/workspace"
                variant="contained"
                color="primary"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{ fontWeight: 500, textTransform: "none", px: 3 }}
              >
                {t("ResumeMasteryLanding.openWorkspaceButton")}
              </Button>
              <Button
                component={RouterLink}
                to="/resume-mastery/ats-score"
                variant="outlined"
                size="large"
                sx={{ color: theme.palette.primary.main, borderColor: alpha(theme.palette.primary.light, 0.4), fontWeight: 500, textTransform: "none", px: 3 }}
              >
                {t("ResumeMasteryLanding.scoreResumeFreeButton")}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* How it works */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Typography variant="h4" fontWeight={500} mb={3}>{t("ResumeMasteryLanding.howItWorksHeading")}</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          {STEPS.map((s) => (
            <Card key={s.title} variant="outlined" sx={{ flex: 1, borderColor: alpha(theme.palette.primary.light, 0.15), borderRadius: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={500} gutterBottom>{s.title}</Typography>
                <Typography variant="body2" color="text.secondary">{s.body}</Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Container>

      {/* Differentiators */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.light, 0.03), py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={500} mb={3}>{t("ResumeMasteryLanding.differentiatorsHeading")}</Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            {DIFFERENTIATORS.map((d) => (
              <Card key={d.title} variant="outlined" sx={{ flex: 1, borderColor: alpha(theme.palette.primary.light, 0.15), borderRadius: 3, bgcolor: "background.paper" }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={500} gutterBottom>{d.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{d.body}</Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Pricing + closing CTA */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 }, textAlign: "center" }}>
        <Typography variant="h4" fontWeight={500} mb={1}>{t("ResumeMasteryLanding.startFreeHeading")}</Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          {t("ResumeMasteryLanding.startFreeBody")}{" "}
          <Button component={RouterLink} to="/pricing" sx={{ textTransform: "none", fontWeight: 500, color: theme.palette.primary.main, p: 0, minWidth: 0 }}>
            {t("ResumeMasteryLanding.seePricingButton")}
          </Button>
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
          <Button component={RouterLink} to="/resume-mastery/workspace" variant="contained" color="primary"
            sx={{ fontWeight: 500, textTransform: "none", px: 3 }}>
            {t("ResumeMasteryLanding.openWorkspaceButton")}
          </Button>
          <Button component={RouterLink} to="/resume-mastery/ats-score" variant="text"
            sx={{ color: theme.palette.primary.main, fontWeight: 500, textTransform: "none" }}>
            {t("ResumeMasteryLanding.scoreResumeFreeButton")}
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}
